import { motion } from 'framer-motion'
import { cn } from '../../lib/utils'

export interface GradientOrbProps {
  /** Color variant of the orb */
  color?: 'copper' | 'sage' | 'midnight' | 'purple'
  /** Size of the orb */
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** Position relative to parent */
  position?: {
    top?: string
    right?: string
    bottom?: string
    left?: string
  }
  /** Blur intensity */
  blur?: 'sm' | 'md' | 'lg' | 'xl'
  /** Opacity (0-100) */
  opacity?: number
  /** Enable floating animation */
  animate?: boolean
  /** Custom className */
  className?: string
}

const colorStyles = {
  copper: 'bg-gradient-to-br from-copper-400/60 via-copper-500/40 to-copper-300/30',
  sage: 'bg-gradient-to-br from-sage-400/60 via-sage-500/40 to-sage-300/30',
  midnight: 'bg-gradient-to-br from-midnight-600/60 via-midnight-700/40 to-midnight-500/30',
  purple: 'bg-gradient-to-br from-purple-400/60 via-purple-500/40 to-purple-300/30',
}

const sizeStyles = {
  sm: 'w-48 h-48',
  md: 'w-72 h-72',
  lg: 'w-96 h-96',
  xl: 'w-[32rem] h-[32rem]',
}

const blurStyles = {
  sm: 'blur-2xl',
  md: 'blur-3xl',
  lg: 'blur-[100px]',
  xl: 'blur-[150px]',
}

export function GradientOrb({
  color = 'copper',
  size = 'md',
  position,
  blur = 'lg',
  opacity = 50,
  animate = true,
  className,
}: GradientOrbProps) {
  const positionStyle = position
    ? {
        top: position.top,
        right: position.right,
        bottom: position.bottom,
        left: position.left,
      }
    : {}

  const orbContent = (
    <div
      className={cn(
        'absolute rounded-full pointer-events-none',
        colorStyles[color],
        sizeStyles[size],
        blurStyles[blur],
        className
      )}
      style={{
        ...positionStyle,
        opacity: opacity / 100,
      }}
    />
  )

  if (!animate) {
    return orbContent
  }

  return (
    <motion.div
      className={cn(
        'absolute rounded-full pointer-events-none',
        colorStyles[color],
        sizeStyles[size],
        blurStyles[blur],
        className
      )}
      style={{
        ...positionStyle,
        opacity: opacity / 100,
      }}
      animate={{
        y: [-10, 10, -10],
        x: [-5, 5, -5],
        scale: [1, 1.05, 1],
      }}
      transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    />
  )
}

/**
 * Pre-configured gradient orb for hero sections
 */
export function HeroGradientOrbs() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <GradientOrb
        color="copper"
        size="xl"
        position={{ top: '-10%', right: '-10%' }}
        opacity={30}
      />
      <GradientOrb
        color="sage"
        size="lg"
        position={{ bottom: '10%', left: '-5%' }}
        opacity={25}
      />
      <GradientOrb
        color="midnight"
        size="md"
        position={{ top: '40%', left: '30%' }}
        opacity={20}
      />
    </div>
  )
}

/**
 * Gradient mesh background for sections
 */
export function GradientMesh({ className }: { className?: string }) {
  return (
    <div className={cn('absolute inset-0 overflow-hidden pointer-events-none', className)}>
      <div className="absolute inset-0 bg-gradient-to-br from-midnight-950 via-midnight-900 to-midnight-800" />
      <GradientOrb
        color="copper"
        size="xl"
        position={{ top: '-20%', right: '-10%' }}
        opacity={20}
        blur="xl"
      />
      <GradientOrb
        color="sage"
        size="xl"
        position={{ bottom: '-20%', left: '-10%' }}
        opacity={15}
        blur="xl"
      />
    </div>
  )
}
